import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAtomValue } from "jotai";
import { Button, Spinner, Tooltip } from "@common/index";
import { ComponentColor } from "@common/types";
import { useNotification } from "@components/Notification/hooks/useNotification";
import { useUserApi } from "@/hooks/apiHooks/useUserApi";
import {
    currentUserAtom,
    isProcessingUserAtom,
    userFetchErrorAtom,
} from "@/store/userAtoms";
import { AccountLoadError } from "@components/Auth/AccountLoadError";
import {
    formatUsPhone,
    prefillUsPhone,
} from "@customer/Marketing/_helpers/formatUsPhone";

// Email is the CIAM sign-in identifier and there is no Entra update path, so it
// is shown read-only with the reason attached rather than silently omitted.
// Deliberately points nowhere: the only candidate destination ("Update Log In"
// on the dashboard) is an aria-disabled "Coming soon" placeholder, and sending
// users to a dead control is worse than saying less.
const EMAIL_TOOLTIP =
    "Email is managed by your sign-in and cannot be changed here.";

const EMAIL_HELP_ID = "profile-email-help";

// Deliberately minimal, and deliberately silent about sign-in: the names land in
// the local User row only, Entra keeps the original for authentication and this
// story has no Entra update path. Anything broader ("your account was updated")
// would promise a change to the login identity that did not happen.
const SAVE_SUCCESS_MESSAGE = "Profile updated.";

// Mirrors DomainConstraint.User on the API side so a length the server would
// reject with a 400 is caught inline instead. Phone is omitted: the mask caps
// input at 14 characters, well inside the server's 32, so a rule could never
// fire — same reasoning as the apply form.
const MAX = {
    firstName: 128,
    lastName: 128,
} as const;

const profileSchema = z.object({
    firstName: z
        .string()
        .trim()
        .nonempty("First name is required")
        .max(MAX.firstName, `First name must be ${MAX.firstName} characters or fewer`),
    lastName: z
        .string()
        .trim()
        .nonempty("Last name is required")
        .max(MAX.lastName, `Last name must be ${MAX.lastName} characters or fewer`),
    // The same mask the apply form produces and UpdateMeUserValidator enforces.
    phoneNumber: z
        .string()
        .trim()
        .nonempty("Phone number is required")
        .regex(
            /^\(\d{3}\) \d{3}-\d{4}$/,
            "Enter a valid 10-digit phone number",
        ),
});

type ProfileFormData = z.infer<typeof profileSchema>;

export const AccountProfile = () => {
    const user = useAtomValue(currentUserAtom);
    const isProcessing = useAtomValue(isProcessingUserAtom);
    const hasFetchError = useAtomValue(userFetchErrorAtom);
    const { getMeUser, updateMeUser } = useUserApi();
    const { showSuccess } = useNotification();

    // The last-saved values. Cancel returns here, and the post-save getMeUser()
    // refresh re-baselines them to what the server actually stored.
    //
    // Keyed on the field VALUES, not the `user` object. Every write to
    // currentUserAtom mints a new object even when the content is byte
    // identical — an MSAL silent token refresh cascading into a refetch is the
    // live trigger — and keying on identity would refire the sync effect below
    // and reset over whatever the user was mid-way through typing. A refetch
    // that changes nothing has to be a no-op; a real content change still
    // resets, which is the behaviour we want.
    const savedValues = useMemo<ProfileFormData>(
        () => ({
            firstName: user?.firstName ?? "",
            lastName: user?.lastName ?? "",
            phoneNumber: prefillUsPhone(user?.phoneNumber),
        }),
        [user?.firstName, user?.lastName, user?.phoneNumber],
    );

    const {
        register,
        handleSubmit,
        setValue,
        reset,
        formState: { errors },
    } = useForm<ProfileFormData>({
        resolver: zodResolver(profileSchema),
        defaultValues: savedValues,
    });

    // currentUserAtom is null until ProtectedRoute's getMeUser() resolves, and
    // gets a fresh object after every save — so the form is synced from here
    // rather than from defaultValues alone.
    useEffect(() => {
        reset(savedValues);
    }, [savedValues, reset]);

    const onSubmit = async (data: ProfileFormData) => {
        try {
            await updateMeUser({
                firstName: data.firstName.trim(),
                lastName: data.lastName.trim(),
                phoneNumber: data.phoneNumber.trim(),
            });

            // Confirms the WRITE, so it fires the moment the write is durable —
            // before the refresh below, not after. getMeUser() is a read that can
            // fail on its own (and raises its own toast when it does); letting
            // that failure suppress this message would tell the user nothing
            // saved when the server has already accepted the change.
            showSuccess({ message: SAVE_SUCCESS_MESSAGE });

            // Re-read the row so every surface bound to currentUserAtom (nav,
            // dashboard greeting) shows the new name instead of stale claims.
            await getMeUser();
        } catch {
            // useApi routes the failure to a toast; keep the edits on screen so
            // the user can retry without retyping.
        }
    };

    return (
        <div className="bg-base-100 text-neutral min-h-[60vh]">
            <div className="max-w-3xl mx-auto px-4 py-6 md:py-8 flex flex-col gap-6">
                <h1 className="text-3xl font-bold text-neutral">Profile</h1>

                {/* The account subtree sits outside ApprovalGate, so the
                    failed /users/me read has to be rendered here — otherwise
                    it is a spinner that never ends. */}
                {!user && hasFetchError && <AccountLoadError />}

                {!user && !hasFetchError && (
                    <div className="flex justify-center items-center py-12">
                        <Spinner size="lg" />
                    </div>
                )}

                {user && (
                    <form
                        onSubmit={handleSubmit(onSubmit)}
                        noValidate
                        className="rounded-2xl bg-base-200 p-6 sm:p-8 flex flex-col gap-6"
                    >
                        <div className="grid gap-6 sm:grid-cols-2">
                            <div>
                                <label
                                    htmlFor="firstName"
                                    className="block font-bold mb-2"
                                >
                                    First Name
                                </label>
                                <input
                                    id="firstName"
                                    type="text"
                                    className="input input-bordered w-full bg-white text-neutral"
                                    {...register("firstName")}
                                />
                                {errors.firstName && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.firstName.message}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label
                                    htmlFor="lastName"
                                    className="block font-bold mb-2"
                                >
                                    Last Name
                                </label>
                                <input
                                    id="lastName"
                                    type="text"
                                    className="input input-bordered w-full bg-white text-neutral"
                                    {...register("lastName")}
                                />
                                {errors.lastName && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.lastName.message}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div>
                            <label
                                htmlFor="phoneNumber"
                                className="block font-bold mb-2"
                            >
                                Phone Number
                            </label>
                            <input
                                id="phoneNumber"
                                type="tel"
                                inputMode="numeric"
                                placeholder="(___) ___-____"
                                className="input input-bordered w-full bg-white text-neutral"
                                {...register("phoneNumber")}
                                onChange={(e) =>
                                    setValue(
                                        "phoneNumber",
                                        formatUsPhone(e.target.value),
                                        { shouldValidate: true },
                                    )
                                }
                            />
                            {errors.phoneNumber && (
                                <p className="text-red-500 text-sm mt-1">
                                    {errors.phoneNumber.message}
                                </p>
                            )}
                        </div>

                        <div>
                            <label
                                htmlFor="email"
                                className="block font-bold mb-2"
                            >
                                Email
                            </label>
                            {/* readOnly rather than disabled: a disabled input is
                                unfocusable, which would put both the tooltip and
                                its description out of reach of the keyboard. */}
                            <Tooltip
                                text={EMAIL_TOOLTIP}
                                orientation="bottom"
                                className="w-full"
                            >
                                <input
                                    id="email"
                                    type="email"
                                    value={user.email}
                                    readOnly
                                    aria-describedby={EMAIL_HELP_ID}
                                    className="input input-bordered w-full bg-base-300 text-neutral cursor-not-allowed"
                                />
                            </Tooltip>
                            {/* data-tip is not exposed to assistive tech, so the
                                same copy is carried as the field's description. */}
                            <span id={EMAIL_HELP_ID} className="sr-only">
                                {EMAIL_TOOLTIP}
                            </span>
                        </div>

                        <div className="flex gap-2">
                            <Button
                                type="submit"
                                color={ComponentColor.Warning}
                                loading={isProcessing}
                                className="text-neutral font-bold normal-case"
                            >
                                {isProcessing ? "Saving..." : "Save"}
                            </Button>
                            <Button
                                color={ComponentColor.Neutral}
                                disabled={isProcessing}
                                onClick={() => reset(savedValues)}
                                className="normal-case"
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};
